"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { datadogRum } from "@datadog/browser-rum";
import { initDatadogRum } from "./rum";
import type { RumConfig } from "./rum";

interface DatadogRumProps extends RumConfig {
  /**
   * URL path prefixes that activate RUM (e.g. ["/contact-sales", "/view-demos"]).
   * Once activated, RUM remains active for the rest of the session — navigating
   * away from these paths does not stop monitoring.
   *
   * Pass a stable reference (module-level constant) to avoid unnecessary effect re-runs.
   */
  activationPaths: string[];
  /**
   * Name of a cookie whose value should be set as the RUM user ID.
   * Re-applied on each in-app navigation within an activation path;
   * safe because `setUser` is idempotent. Omit to leave sessions anonymous.
   */
  userIdCookieName?: string;
}

/**
 * Activates Datadog RUM when the user visits a matching path.
 * Place in your root layout — it reacts to client-side navigations automatically.
 *
 * Once RUM is initialized it captures the full session; `activationPaths` controls
 * which pages *trigger* initialization, not which pages are monitored.
 *
 * @example
 * ```tsx
 * import { DatadogRum } from "@marketing-sites/datadog/rum-provider";
 *
 * const RUM_PATHS = ["/contact-sales", "/view-demos"];
 *
 * <DatadogRum activationPaths={RUM_PATHS} />
 * ```
 */
export function DatadogRum({
  activationPaths,
  userIdCookieName,
  sessionSampleRate,
  sessionReplaySampleRate,
  trackUserInteractions,
  trackResources,
}: DatadogRumProps) {
  const pathname = usePathname();

  useEffect(() => {
    if (activationPaths.some((prefix) => pathname.startsWith(prefix))) {
      const initialized = initDatadogRum({
        sessionSampleRate,
        sessionReplaySampleRate,
        trackUserInteractions,
        trackResources,
      });

      if (initialized && userIdCookieName) {
        const prefix = `${userIdCookieName}=`;
        const userId = document.cookie
          .split("; ")
          .find((c) => c.startsWith(prefix))
          ?.slice(prefix.length);

        if (userId) {
          datadogRum.setUser({ id: userId });
        }
      }
    }
  }, [
    pathname,
    activationPaths,
    userIdCookieName,
    sessionSampleRate,
    sessionReplaySampleRate,
    trackUserInteractions,
    trackResources,
  ]);

  return null;
}
